import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { HeaderMenuStyle } from "@/lib/navDefaults";

const KEY = ["header-menu-style"];

/** Active public header menu. Anything other than "mega" (incl. read errors) keeps the simple menu. */
export function useHeaderMenuStyle() {
  const { data } = useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<HeaderMenuStyle> => {
      const { data } = await supabase
        .from("admin_settings")
        .select("value")
        .eq("key", "header_menu_style")
        .maybeSingle();
      return data?.value === "mega" ? "mega" : "simple";
    },
    staleTime: 30 * 60 * 1000,
  });
  // Preview link from Theme Options: shows the mega menu in this tab only, never changes the live setting.
  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("menu_preview") === "mega") {
    return "mega";
  }
  return data ?? "simple";
}

/** Admin-only write (admin_settings policies gate it). */
export function useSetHeaderMenuStyle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (style: HeaderMenuStyle) => {
      const { error } = await supabase
        .from("admin_settings")
        .update({ value: style })
        .eq("key", "header_menu_style");
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
